/* Shared lifecycle follows the supplied Sparkle section: local queries, abortable listeners and disconnect cleanup. */
(() => {
  if (customElements.get('yip-original-experience-v3')) return;
  class YipOriginalExperienceV3 extends HTMLElement {
    connectedCallback() {
      if (this._controller) return;
      this._controller = new AbortController();
      this._signal = this._controller.signal;
      this._observers = [];
      this._timers = new Set();
      this._frames = new Set();
      this.reduce = matchMedia('(prefers-reduced-motion: reduce)');
      this.$ = (selector) => this.querySelector(selector);
      this.$$ = (selector) => [...this.querySelectorAll(selector)];
      this.panels = this.$$('[data-panel]');
      this.tabs = this.$$('[data-tab]');
      this.index = 0;
      this.words = JSON.parse(this.querySelector("[data-interface]")?.textContent || "{}");
      this.setupHeadlines();
      this.setupTabs();
      this.setupPhotos();
      this.setupTilt();
      this.setupDetails();
      this.setupHotspots();
      this.setupModels();
      this.setupFlow();
      this.setupCart();
      this.setupSticky();
      this.setupEditor();
      this.updateHeaderOffset();
      this.on(window, 'resize', () => this.updateHeaderOffset(), {passive:true});
      this.on(this.reduce, 'change', () => {
        this.$$('[data-tilt]').forEach(el => ['--yp3-rx','--yp3-ry','--yp3-lift','--yp3-shine'].forEach(p => el.style.removeProperty(p)));
        this.$$('[data-reveal]').forEach(el=>el.classList.remove('yp3-waiting'));
        this.style.removeProperty('--yp3-scroll');
      });
      this.setAttribute('data-ready', '');
      // A shared bottle/format link can open directly at the correct panel.
      const hash = location.hash.slice(1);
      const i = this.panels.findIndex(p => p.id === hash);
      if (i >= 0) this.select(i, {announce:false});
    }
    disconnectedCallback() {
      this._controller?.abort();
      this._controller = null;
      this._observers?.forEach(o => o.disconnect());
      this._timers?.forEach(clearTimeout);
      this._frames?.forEach(cancelAnimationFrame);
      this._models?.forEach(state=>{
        state.generation++;
        state.cancelLoad?.();
        state.ui?.pause?.();
        state.ui?.destroy?.();
        state.stage.replaceChildren();
        state.stage.hidden=true;
        state.shell.classList.remove('is-model-active');
        if(state.photo) state.photo.hidden=false;
      });
      this.closeHotspots(false);
      if (this.dialog?.open) this.dialog.close();
      this.unlock();
      this.panels?.forEach(p => {p.hidden=false; p.removeAttribute('role');});
      this.sticky?.classList.remove('is-visible');
      this.sticky?.setAttribute('inert','');
      this.removeAttribute('data-ready');
    }
    get motion() { return this.dataset.motion !== 'off' && !this.reduce.matches; }
    on(el, event, fn, options={}) { el?.addEventListener(event, fn, {...options, signal:this._signal}); }
    frame(fn) {
      const id = requestAnimationFrame(() => {this._frames.delete(id); if (this.isConnected) fn();});
      this._frames.add(id); return id;
    }
    observe(targets, callback, options={}) {
      if (!('IntersectionObserver' in window)) return null;
      const io = new IntersectionObserver(callback, options);
      targets.filter(Boolean).forEach(el => io.observe(el));
      this._observers.push(io); return io;
    }
    updateHeaderOffset() {
      let offset = Number(this.dataset.scrollOffset || 0);
      const header = document.querySelector('#site-header, header.site-header, [data-sticky-header], .shopify-section-header-sticky');
      for (let el=header, n=0; el && n<4; el=el.parentElement, n++) {
        const pos=getComputedStyle(el).position;
        if (pos==='fixed' || pos==='sticky') {offset=Math.max(offset, Math.round(el.getBoundingClientRect().height));break;}
      }
      this.style.setProperty('--yp3-header', `${offset}px`);
    }
    setupTabs() {
      if (!this.panels.length) return;
      this.$('[data-tablist]')?.setAttribute('role','tablist');
      this.tabs.forEach((tab,i) => {
        tab.setAttribute('role','tab');
        tab.setAttribute('aria-controls',this.panels[i]?.id || '');
        this.on(tab,'click',e => {
          if (e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault(); this.select(i);
        });
        this.on(tab,'keydown',e => {
          let next = null;
          if (e.key==='ArrowRight') next=(i+1)%this.tabs.length;
          else if (e.key==='ArrowLeft') next=(i-1+this.tabs.length)%this.tabs.length;
          else if (e.key==='Home') next=0;
          else if (e.key==='End') next=this.tabs.length-1;
          else if (e.key===' ') {e.preventDefault();this.select(i);return;}
          if (next===null) return;
          e.preventDefault(); this.select(next);this.tabs[next].focus({preventScroll:true});
        });
      });
      this.panels.forEach((panel,i) => {
        panel.setAttribute('role','tabpanel');
        panel.setAttribute('aria-labelledby',this.tabs[i]?.id || '');
        panel.tabIndex=0;
      });
      this.$$('[data-select]').forEach(link => this.on(link,'click',e => {
        if (e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault(); this.select(Number(link.dataset.select));
        this.jumpToChooser();
      }));
      this.$$('[data-jump-chooser]').forEach(link => this.on(link,'click',e => {
        if (e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault(); this.jumpToChooser();
      }));
      this.panels.forEach(panel => {
        const media=panel.querySelector('[data-swipe]');
        let start=null;
        this.on(media,'pointerdown',e => {
          if(e.pointerType==='mouse') return;
          start={x:e.clientX,y:e.clientY};
        });
        this.on(media,'pointerup',e => {
          if(!start) return;
          const dx=e.clientX-start.x,dy=e.clientY-start.y;start=null;
          if(Math.abs(dx)>60 && Math.abs(dx)>Math.abs(dy)*1.5) {
            this._suppressPhotoClick=true;
            const timer=setTimeout(()=>{this._timers.delete(timer);this._suppressPhotoClick=false;},350);this._timers.add(timer);
            this.select((this.index+(dx<0?1:-1)+this.panels.length)%this.panels.length);
          }
        });
        this.on(media,'pointercancel',()=>{start=null;});
      });
      this.select(0,{announce:false});
    }
    select(i,{announce=true}={}) {
      if(!this.panels[i]) return;
      this.index=i;
      this.panels.forEach((p,n)=>{p.hidden=n!==i;});
      this.tabs.forEach((t,n)=>{t.setAttribute('aria-selected',String(n===i));t.tabIndex=n===i?0:-1;});
      this._models?.forEach(state=>{
        if(state.shell.closest('[data-panel]')?.hidden) this.hideModel(state,false);
      });
      this.$$('[data-select]').forEach(link=>{
        link.classList.toggle('is-current',Number(link.dataset.select)===i);
      });
      const p=this.panels[i];
      if(this.$('[data-sticky-name]')) this.$('[data-sticky-name]').textContent=p.dataset.name;
      if(this.$('[data-sticky-detail]')) this.$('[data-sticky-detail]').textContent=p.dataset.detail || '';
      if(announce && this.$('[data-selection-live]')) this.$('[data-selection-live]').textContent=`${this.words.selected || ''} ${p.dataset.name}.`;
      if(this._syncSticky) this._syncSticky();
    }
    jumpToChooser() {
      const chooser=this.$('[data-chooser]');
      chooser?.scrollIntoView({behavior:this.motion?'smooth':'auto',block:'start'});
      const tab=this.tabs[this.index];
      tab?.focus({preventScroll:true});
    }
    setupTilt() {
      if(!matchMedia('(hover:hover) and (pointer:fine)').matches) return;
      this.$$('[data-tilt]').forEach(zone => {
        let rect=null, raf=0, x=0,y=0;
        this.on(zone,'pointerenter',()=>{if(this.motion) rect=zone.getBoundingClientRect();});
        this.on(zone,'pointermove',e => {
          if(!rect || !this.motion) return;
          x=Math.max(-1,Math.min(1,(e.clientX-rect.left)/rect.width*2-1));
          y=Math.max(-1,Math.min(1,(e.clientY-rect.top)/rect.height*2-1));
          if(!raf) raf=this.frame(()=>{raf=0;zone.style.setProperty('--yp3-shine',String(x));zone.style.setProperty('--yp3-ry',`${x*5}deg`);zone.style.setProperty('--yp3-rx',`${y*-3}deg`);});
        });
        const reset=()=>{
          rect=null; if(raf) {cancelAnimationFrame(raf);this._frames.delete(raf);raf=0;}
          ['--yp3-ry','--yp3-rx','--yp3-shine'].forEach(p=>zone.style.removeProperty(p));
        };
        this.on(zone,'pointerleave',reset);this.on(zone,'pointercancel',reset);
      });
    }
    setupDetails() {
      this.$$('[data-detail-stack]').forEach(stack=>{
        const details=[...stack.querySelectorAll('details')];
        details.forEach(item=>this.on(item,'toggle',()=>{
          if(item.open) details.forEach(other=>{if(other!==item) other.open=false;});
        }));
      });
    }
    setupHotspots() {
      this.hotspotButtons=this.$$('[data-hotspot]');
      this.hotspotPanels=this.$$('[data-hotspot-panel]');
      this.hotspotButtons.forEach(button=>this.on(button,'click',()=>{
        const opening=button.getAttribute('aria-expanded')!=='true';
        this.closeHotspots(false);
        if(!opening) return;
        const panel=this.hotspotPanels.find(p=>p.dataset.hotspotPanel===button.dataset.hotspot);
        if(!panel) return;
        this.hotspotOpener=button;
        button.setAttribute('aria-expanded','true');
        panel.hidden=false;
        panel.querySelector('[data-close-hotspot]')?.focus({preventScroll:true});
      }));
      this.$$('[data-close-hotspot]').forEach(button=>this.on(button,'click',()=>this.closeHotspots()));
      this.on(this,'keydown',event=>{
        if(event.key==='Escape' && this.hotspotPanels.some(p=>!p.hidden)) {
          event.preventDefault();this.closeHotspots();
        }
      });
      this.on(document,'pointerdown',event=>{
        if(!event.target.closest('[data-hotspots]')) this.closeHotspots(false);
      });
      this.hotspotPanels.forEach(panel=>{
        panel.querySelectorAll('a').forEach(a=>this.on(a,'click',()=>this.closeHotspots(false)));
      });
    }
    closeHotspots(restore=true) {
      this.hotspotButtons?.forEach(b=>b.setAttribute('aria-expanded','false'));
      this.hotspotPanels?.forEach(p=>{p.hidden=true;});
      if(restore && this.hotspotOpener?.isConnected) this.hotspotOpener.focus({preventScroll:true});
      this.hotspotOpener=null;
    }
    setupFlow() {
      const targets=this.$$('[data-reveal]');
      if(this.motion && 'IntersectionObserver' in window) {
        targets.forEach(el=>el.classList.add('yp3-waiting'));
        const observer=this.observe(targets,entries=>{
          entries.forEach(entry=>{
            if(entry.isIntersecting){entry.target.classList.remove('yp3-waiting');observer?.unobserve(entry.target);}
          });
        },{threshold:.08});
      }
      let scheduled=false;
      const update=()=>{
        if(!this.motion || scheduled) return;
        scheduled=true;
        this.frame(()=>{
          scheduled=false;
          const r=this.getBoundingClientRect();
          if(r.bottom<0 || r.top>innerHeight) return;
          this.style.setProperty('--yp3-scroll',String(Math.max(0,Math.min(1,-r.top/innerHeight))));
        });
      };
      this.on(window,'scroll',update,{passive:true});
      update();
    }
    static loadModelFeatures() {
      if(this.modelFeatures) return this.modelFeatures;
      if(!window.Shopify?.loadFeatures) return Promise.reject(new Error('Model viewer unavailable'));
      if(!document.querySelector('link[data-yip-v2-model-css], link[href*="model-viewer-ui.css"]')) {
        const sheet=document.createElement('link');
        sheet.rel='stylesheet';sheet.href='https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css';
        sheet.dataset.yipV2ModelCss='';document.head.append(sheet);
      }
      this.modelFeatures=new Promise((resolve,reject)=>{
        const timeout=setTimeout(()=>reject(new Error('Model viewer timed out')),12000);
        window.Shopify.loadFeatures([{name:'model-viewer-ui',version:'1.0',onLoad:errors=>{
          clearTimeout(timeout);
          if(errors || !window.Shopify.ModelViewerUI) reject(new Error('Model viewer unavailable'));
          else resolve();
        }}]);
      }).catch(error=>{this.modelFeatures=null;throw error;});
      return this.modelFeatures;
    }
    setupModels() {
      this._models=this.$$('[data-model]').map(host=>{
        const shell=host.closest('[data-media-shell]');
        const state={host,shell,photo:shell.querySelector('[data-photo-view]'),stage:host.querySelector('[data-model-stage]'),
          open:host.querySelector('[data-show-model]'),close:host.querySelector('[data-hide-model]'),
          status:host.querySelector('[data-model-status]'),generation:0};
        this.on(state.open,'click',()=>this.showModel(state));
        this.on(state.close,'click',()=>this.hideModel(state,true));
        return state;
      });
    }
    async showModel(state) {
      if(state.loading) return;
      const generation=++state.generation;
      state.loading=true;
      state.open.disabled=true;
      state.status.textContent=this.words.model_loading;
      this.closeHotspots(false);
      const current=()=>this.isConnected && generation===state.generation;
      try {
        if(!state.viewer) {
          state.stage.append(state.host.querySelector('template').content.cloneNode(true));
          state.viewer=state.stage.querySelector('model-viewer');
          if(!state.viewer) throw new Error('Model missing');
          state.viewer.setAttribute('aria-label',this.words.model_help+' '+(state.stage.getAttribute('aria-label') || 'the product'));
          state.viewer.tabIndex=0;
        }
        const modelLoaded=new Promise((resolve,reject)=>{
          if(state.viewer.loaded) {resolve();return;}
          const done=()=>{
            clearTimeout(timeout);this._timers.delete(timeout);
            state.viewer?.removeEventListener('load',success);
            state.viewer?.removeEventListener('error',failure);
          };
          const success=()=>{done();resolve();};
          const failure=()=>{done();reject(new Error('Model could not load'));};
          const timeout=setTimeout(failure,20000);this._timers.add(timeout);
          this.on(state.viewer,'load',success,{once:true});
          this.on(state.viewer,'error',failure,{once:true});
          state.cancelLoad=()=>{done();resolve();};
        });
        state.stage.hidden=false;
        state.stage.setAttribute('data-loading','');
        await Promise.all([
          modelLoaded,
          this.constructor.loadModelFeatures().then(()=>{
            if(current() && !state.ui) state.ui=new window.Shopify.ModelViewerUI(state.viewer);
          })
        ]);
        if(!current()) return;
        state.stage.removeAttribute('data-loading');
        state.shell.classList.add('is-model-active');
        if(state.photo) state.photo.hidden=true;
        state.open.hidden=true;state.close.hidden=false;
        state.open.setAttribute('aria-expanded','true');
        state.status.textContent='';
        state.ui?.play?.();
        if(!this.dialog?.open) state.viewer.focus({preventScroll:true});
      } catch(error) {
        if(!current()) return;
        this.hideModel(state,false);
        state.ui?.destroy?.();state.ui=null;state.viewer=null;state.stage.replaceChildren();
        state.status.textContent=this.words.model_error;
        state.open.focus({preventScroll:true});
      } finally {
        if(current()) {state.loading=false;state.open.disabled=false;}
      }
    }
    hideModel(state,restore=false) {
      state.generation++;state.loading=false;
      state.cancelLoad?.();state.cancelLoad=null;
      state.ui?.pause?.();
      state.stage.hidden=true;
      state.stage.removeAttribute('data-loading');
      state.shell.classList.remove('is-model-active');
      if(state.photo) state.photo.hidden=false;
      state.open.hidden=false;state.open.disabled=false;state.close.hidden=true;
      state.open.setAttribute('aria-expanded','false');
      state.status.textContent='';
      if(restore) state.open.focus({preventScroll:true});
    }
    setupPhotos() {
      this.dialog=this.$('[data-lightbox]');
      if(!this.dialog || typeof this.dialog.showModal!=='function') return;
      this.lbImage=this.dialog.querySelector('[data-lb-image]');
      this.lbTitle=this.dialog.querySelector('[data-lb-title]');
      this.lbCaption=this.dialog.querySelector('[data-lb-caption]');
      this.lbCount=this.dialog.querySelector('[data-lb-count]');
      this.photoLinks=this.$$('[data-photo]');
      this.photoLinks.forEach(link => {
        link.setAttribute('aria-haspopup','dialog');
        this.on(link,'click',e => {
          if(this._suppressPhotoClick) {e.preventDefault();this._suppressPhotoClick=false;return;}
          if(e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          this._opener=link;
          this.lbSet=this.photoLinks.filter(x=>x.dataset.group===link.dataset.group);
          this.showPhoto(this.lbSet.indexOf(link));
          this.dialog.showModal();
          this._overflow=document.documentElement.style.overflow;
          document.documentElement.style.overflow='hidden';this._locked=true;
          this.dialog.querySelector('[data-lb-close]')?.focus({preventScroll:true});
          this._syncSticky?.();
        });
      });
      this.on(this.dialog.querySelector('[data-lb-close]'),'click',()=>this.dialog.close());
      this.on(this.dialog,'cancel',()=>this.unlock());
      this.on(this.dialog,'close',()=>{
        this.unlock();this.resetZoom();this._syncSticky?.();
        if(this._opener?.isConnected && !this._opener.closest('[hidden]')) this._opener.focus({preventScroll:true});
      });
      this.on(this.dialog,'click',e=>{
        if(e.target!==this.dialog) return;
        const r=this.dialog.getBoundingClientRect();
        if(e.clientX<r.left || e.clientX>r.right || e.clientY<r.top || e.clientY>r.bottom) this.dialog.close();
      });
      this.on(this.dialog,'keydown',e=>{
        if(this.dialog.hasAttribute('data-zoomed')) return;
        if(e.key==='ArrowLeft' || e.key==='ArrowRight') {
          e.preventDefault();this.showPhoto(this.lbIndex+(e.key==='ArrowRight'?1:-1));
        }
      });
      this.dialog.querySelectorAll('[data-lb-step]').forEach(b=>this.on(b,'click',()=>this.showPhoto(this.lbIndex+Number(b.dataset.lbStep))));
      const zoom=this.dialog.querySelector('[data-lb-zoom]');
      this.on(zoom,'click',()=>{
        const active=this.dialog.toggleAttribute('data-zoomed');
        zoom.setAttribute('aria-pressed',String(active));
        zoom.querySelector('span').textContent=active?this.words.fit:this.words.zoom;
        const stage=this.dialog.querySelector('[data-lb-stage]');
        stage.scrollTop=0;stage.scrollLeft=0;
        if(active) stage.focus({preventScroll:true});
      });
      let start=null;
      const stage=this.dialog.querySelector('[data-lb-stage]');
      this.on(stage,'pointerdown',e=>{if(e.pointerType!=='mouse' && !this.dialog.hasAttribute('data-zoomed')) start={x:e.clientX,y:e.clientY};});
      this.on(stage,'pointerup',e=>{
        if(!start) return;const dx=e.clientX-start.x,dy=e.clientY-start.y;start=null;
        if(Math.abs(dx)>60 && Math.abs(dx)>Math.abs(dy)*1.5) this.showPhoto(this.lbIndex+(dx<0?1:-1));
      });
      this.on(stage,'pointercancel',()=>{start=null;});
      this.on(this.lbImage,'error',()=>{
        this.lbCaption.textContent=this.words.image_error;
      });
      this.on(this.lbImage,'load',()=>{
        this.lbImage.width=this.lbImage.naturalWidth;
        this.lbImage.height=this.lbImage.naturalHeight;
      });
    }
    showPhoto(i) {
      if(!this.lbSet?.length) return;
      this.lbIndex=(i+this.lbSet.length)%this.lbSet.length;
      const link=this.lbSet[this.lbIndex];
      this.resetZoom();
      this.lbImage.src=link.href;
      this.lbImage.alt=link.dataset.alt || link.querySelector('img')?.alt || '';
      this.lbTitle.textContent=link.dataset.title || this.words.closer;
      this.lbCaption.textContent=link.dataset.caption || '';
      this.lbCount.textContent=`${this.lbIndex+1} / ${this.lbSet.length}`;
      this.dialog.querySelectorAll('[data-lb-step]').forEach(b=>{b.disabled=this.lbSet.length<2;});
      this.dialog.setAttribute('aria-label',this.lbTitle.textContent);
    }
    resetZoom() {
      this.dialog?.removeAttribute('data-zoomed');
      const z=this.dialog?.querySelector('[data-lb-zoom]');
      z?.setAttribute('aria-pressed','false');
      if(z) z.querySelector('span').textContent=this.words.zoom;
    }
    unlock() {
      if(!this._locked) return;
      document.documentElement.style.overflow=this._overflow || '';this._locked=false;
    }
    setupCart() {
      this.$$('[data-qty]').forEach(b=>this.on(b,'click',()=>{
        const input=b.closest('.yp3-qty')?.querySelector('input');if(!input) return;
        const min=Number(input.min)||1,max=Number(input.max)||99,step=Number(input.step)||1;
        input.value=String(Math.max(min,Math.min(max,(parseInt(input.value,10)||min)+Number(b.dataset.qty)*step)));
        input.dispatchEvent(new Event('change',{bubbles:true}));
      }));
      this.$$('[data-variant]').forEach(select=>{
        this.on(select,'change',()=>this.applyVariant(select.closest('[data-buy]'),select.selectedOptions[0]));
        this.applyVariant(select.closest('[data-buy]'),select.selectedOptions[0]);
      });
      this.$$('form[data-product-form]').forEach(form=>this.on(form,'submit',e=>{
        if(!window.fetch || !window.FormData || !form.querySelector('[name="id"]')?.value) return;
        if(this.dataset.preview==='true') {e.preventDefault();const status=form.closest('[data-buy]')?.querySelector('[data-cart-status]');if(status) status.textContent=this.words.preview_message;return;}
        e.preventDefault();this.addToCart(form,e.submitter || form.querySelector('[type="submit"]'));
      }));
    }
    applyVariant(buy,option) {
      if(!buy || !option) return;
      const available=option.dataset.available==='true';
      const button=buy.querySelector('[data-add]');
      if(button) {button.disabled=!available || !!buy.querySelector('form[data-busy]');button.querySelector('[data-add-label]').textContent=available?this.words.add:this.words.sold_out;}
      if(buy.querySelector('[data-price]')) buy.querySelector('[data-price]').textContent=option.dataset.price;
      const qty=buy.querySelector('[name="quantity"]');
      if(qty) {qty.min=option.dataset.min || '1';qty.step=option.dataset.increment || '1';qty.max=option.dataset.max || '99';qty.value=qty.min;}
      const note=buy.querySelector('[data-soldout-note]');if(note) note.hidden=available;
    }
    async addToCart(form,button) {
      if(form.dataset.busy) return;
      const status=form.closest('[data-buy]')?.querySelector('[data-cart-status]');
      const label=button?.querySelector('[data-add-label]');
      const original=this.words.add;
      // A second intentional addition must not inherit the previous "Added" label,
      // or let the previous feedback timer overwrite a new in-flight state.
      if(form._feedbackTimer) {clearTimeout(form._feedbackTimer);this._timers.delete(form._feedbackTimer);}
      const variant=form.querySelector('[name="id"]')?.value;
      form.dataset.busy='true';
      if(button) {button.disabled=true;button.setAttribute('aria-busy','true');}
      if(label) label.textContent=this.words.adding;
      if(status) {status.replaceChildren();status.classList.remove('is-error');}
      let serverRejected=false;
      try {
        const response=await fetch(this.dataset.cartAdd || '/cart/add.js',{
          method:'POST',headers:{Accept:'application/json','X-Requested-With':'XMLHttpRequest'},
          body:new FormData(form),credentials:'same-origin',signal:this._signal
        });
        const data=await response.json();
        if(!response.ok || data.status) {serverRejected=true;throw new Error(data.description || data.message || this.words.cart_error);}
        if(this.dataset.afterAdd==='cart') {location.assign(this.dataset.cart || '/cart');return;}
        if(label) label.textContent=this.words.added;
        if(status) {
          status.textContent=this.words.success;
          const link=document.createElement('a');link.href=this.dataset.cart || '/cart';link.textContent=this.words.view_cart;status.append(link);
        }
        this.refreshCart();
        const timer=setTimeout(()=>{this._timers.delete(timer);form._feedbackTimer=null;if(label?.isConnected && !form.dataset.busy && form.querySelector('[name="id"]')?.value===variant && !button.disabled) label.textContent=original;},2600);this._timers.add(timer);form._feedbackTimer=timer;
      } catch(error) {
        if(error.name==='AbortError') return;
        if(label) label.textContent=original;
        if(status) {
          status.classList.add('is-error');
          status.textContent=serverRejected?error.message:this.words.connection_error;
          const link=document.createElement('a');link.href=this.dataset.cart || '/cart';link.textContent=this.words.view_cart;status.append(link);
        }
      } finally {
        delete form.dataset.busy;
        if(button?.isConnected) {
          button.removeAttribute('aria-busy');
          const current=form.querySelector('[data-variant]')?.selectedOptions[0];
          button.disabled=current?.dataset.available==='false';
          if(current && (form.querySelector('[name="id"]')?.value!==variant || !current.dataset.available || button.disabled)) this.applyVariant(form.closest('[data-buy]'),current);
        }
      }
    }
    async refreshCart() {
      try {
        const r=await fetch(`${this.dataset.cart || '/cart'}.js`,{headers:{Accept:'application/json'},credentials:'same-origin',signal:this._signal});
        if(!r.ok) return;
        const cart=await r.json();
        document.querySelectorAll('[data-header-cart-count], [data-cart-count], .cart-count, .cart-count-bubble span[aria-hidden="true"]').forEach(el=>{el.textContent=cart.item_count;});
        // Local 4.2.1 uses helper-cart + renderHelperCart to keep its existing drawer in sync.
        if(this.dataset.cartSection) {
          const section=await fetch(this.dataset.cartSection,{credentials:'same-origin',signal:this._signal});
          if(section.ok) {
            const html=await section.text();
            if(document.getElementById('AjaxCartForm') && typeof window.renderHelperCart==='function') window.renderHelperCart(html);
            else {
              const doc=new DOMParser().parseFromString(html,'text/html');
              const total=doc.querySelector('[data-cart-total]')?.textContent;
              if(total) document.querySelectorAll('[data-header-cart-total]').forEach(el=>{el.textContent=total;});
            }
          }
        }
        ['cart:refresh','cart:updated','cart:change'].forEach(name=>document.dispatchEvent(new CustomEvent(name,{bubbles:true,detail:{cart,source:`yip-${this.dataset.page}`}})));
      } catch(error) { /* The visible View cart link remains the reliable fallback. */ }
    }
    setupSticky() {
      this.sticky=this.$('[data-sticky]');
      if(!this.sticky || !('IntersectionObserver' in window)) return;
      const hero=this.$('[data-hero]'),finish=this.$('[data-finish]');
      const buys=this.$$('[data-buy]');
      const seen=new Map(buys.map(b=>[b,false]));
      let past=false,ending=false,inRoot=true;
      this._syncSticky=()=>{
        const buying=buys.some(b=>seen.get(b) && !b.closest('[hidden]'));
        const show=inRoot && past && !ending && !buying && !this.dialog?.open;
        this.sticky.classList.toggle('is-visible',show);this.sticky.toggleAttribute('inert',!show);
      };
      this.observe([hero,finish,...buys],entries=>{
        entries.forEach(e=>{
          if(e.target===hero) past=!e.isIntersecting && e.boundingClientRect.top<0;
          else if(e.target===finish) ending=e.isIntersecting || e.boundingClientRect.top<0;
          else seen.set(e.target,e.isIntersecting);
        });this._syncSticky();
      },{threshold:0,rootMargin:'0px 0px -12% 0px'});
      this.observe([this],([entry])=>{inRoot=entry.isIntersecting;this._syncSticky();});
    }
    setupHeadlines() {
      const heading=this.$('h1');
      if(!heading) return;
      const fit=()=>{
        const width=heading.clientWidth;
        if(!width) return;
        heading.querySelectorAll('.yp3-fit-line').forEach(line=>{
          line.style.removeProperty('--yp3-fit');
          const full=line.scrollWidth;
          if(full>width) line.style.setProperty('--yp3-fit',String(Math.max(.25,(width-2)/full)));
        });
      };
      const schedule=()=>this.frame(fit);
      this.on(window,'resize',schedule,{passive:true});
      this.on(document.fonts,'loadingdone',schedule);
      document.fonts?.ready.then(()=>{if(this.isConnected && this._controller) schedule();});
      if('ResizeObserver' in window) {
        const ro=new ResizeObserver(schedule);ro.observe(heading);this._observers.push(ro);
      }
      fit();
    }
    setupEditor() {
      if(!window.Shopify?.designMode) return;
      this.on(document,'shopify:block:select',e=>{
        if(e.detail.sectionId!==this.dataset.section) return;
        const id=e.detail.blockId;
        const i=this.panels.findIndex(p=>p.dataset.block===id);
        if(i>=0) this.select(i,{announce:false});
        const target=this.$$('[data-editor-block]').find(el=>el.dataset.editorBlock===id);
        const hotspot=this.$$('[data-hotspot-block]').find(el=>el.dataset.hotspotBlock===id)?.querySelector('[data-hotspot]');
        if(hotspot && hotspot.getAttribute('aria-expanded')!=='true') hotspot.click();
        const history=target?.closest('details');if(history) history.open=true;
        target?.scrollIntoView({behavior:'auto',block:'center'});
      });
      this.on(document,'shopify:block:deselect',e=>{
        if(e.detail.sectionId===this.dataset.section) this.closeHotspots(false);
      });
    }

  }
  customElements.define('yip-original-experience-v3',YipOriginalExperienceV3);
})();
